import type { HTTPService } from "@soft-db/http";
import type { BatchRecipientsUpdate, Recipients } from "./recipients";
import type { Coordinates } from "./shared";
import type { ObjectSummary, StationStatus } from "./station-api";

export type Site = {
	id: string;
	legacyId: string;
	name: string;
	description: string;
	coordinates: Coordinates;
	imageUrl: string;
	station: StationSummary | null;
	timezone: string;
	distributor: string;
	// TODO: Delete when api change
	organization: string;
	spaceOwner: string | null;
	spaces: string[];
	archived: boolean;
	recipients: Recipients;
	inFavorite: boolean;
	isSubscribed: boolean;

	// ---Relations count info---

	alerts: number;
	vibrationCriterias: number;
	weeklyLimits: number;
	dynamicImages: number;
	dashboards: number;
};

export type StationSummary = {
	serialNumber: string;
	status: StationStatus;
} & ObjectSummary;

export type SiteFile = {
	id: string;
	name: string;
	mimeType: string;
	url: string;
};

export type CreateSite = {
	name: string;
	description: string;
};

export type UpdateSite = {
	name?: string;
	description?: string;
	coordinates?: Coordinates;
	recipients?: BatchRecipientsUpdate;
};

export class SiteApi {
	constructor(private _http: HTTPService) {}

	async fetchContext() {
		return await this._http.get<Site[]>(`sites`);
	}

	async fetchAll() {
		return await this._http.get<Site[]>(`sites`, { params: { all: true } });
	}

	async fetchFavorites() {
		return await this._http.get<Site[]>(`sites/favorites`);
	}

	async fetch(id: string) {
		return await this._http.get<Site>(`sites/${id}`);
	}

	async fetchDist(id: string) {
		return await this._http.get<Site[]>(`distributors/${id}/sites`);
	}

	async fetchSpace(id: string) {
		return await this._http.get<Site[]>(`spaces/${id}/sites`);
	}

	async fetchForPublicPage(id: string) {
		return await this._http.get<Site[]>(`public-pages/${id}/sites`);
	}

	async createWithDist(id: string, site: CreateSite) {
		return this._http.post<Site>(`distributors/${id}/sites`, site);
	}

	async createWithSpace(id: string, site: CreateSite) {
		return this._http.post<Site>(`spaces/${id}/sites`, site);
	}

	async update(id: string, data: UpdateSite) {
		return await this._http.patch<Site>(`sites/${id}`, data);
	}

	async archive(id: string) {
		return await this._http.delete<Site>(`sites/${id}`);
	}

	async restore(id: string) {
		return await this._http.post<Site>(`sites/${id}/restore`);
	}

	async updateImage(id: string, file: File) {
		return await this._http.postFile<Site>(`sites/${id}/image`, "file", file);
	}

	async deleteImage(id: string) {
		return await this._http.delete<Site>(`sites/${id}/image`);
	}

	async fetchFile(id: string, fileId: string) {
		return this._http.get<SiteFile>(`sites/${id}/files/${fileId}`);
	}

	async uploadFile(id: string, file: File) {
		const form = new FormData();
		form.append("file", file);
		form.append("name", file.name);
		return this._http.post<SiteFile>(`sites/${id}/files`, form);
	}

	async deleteFile(id: string, fileId: string) {
		return this._http.delete<Site>(`sites/${id}/files/${fileId}`);
	}
}
